import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { evaluateOrdinance, formatTimeLabel } from '../../utils/lawnOrdinance';

/**
 * Today's watering rule for the property's municipality.
 *
 * The allowed/not-allowed answer is derived by `evaluateOrdinance`, and the
 * underlying rule text is always shown so the verdict is auditable.
 */
export default function OrdinanceWidget({ ordinance, address, isLoading }) {
  const verdict = useMemo(
    () => evaluateOrdinance(ordinance, { address }),
    [ordinance, address]
  );

  if (isLoading) {
    return <p className="lawn-widget-muted">Loading ordinance…</p>;
  }

  const today = new Date().toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });

  const statusClass =
    verdict.allowed === null
      ? 'lawn-ordinance-unknown'
      : verdict.allowed
      ? 'lawn-ordinance-allowed'
      : 'lawn-ordinance-blocked';

  return (
    <div className="lawn-ordinance">
      <p className="lawn-ordinance-date">{today}</p>

      <div className={`lawn-ordinance-status ${statusClass}`}>
        <i
          className={
            verdict.allowed === null
              ? 'ri-question-line'
              : verdict.allowed
              ? 'ri-checkbox-circle-line'
              : 'ri-close-circle-line'
          }
          aria-hidden="true"
        ></i>
        <span>{verdict.headline}</span>
      </div>

      <p className="lawn-ordinance-reason">{verdict.reason}</p>

      {ordinance && (
        <dl className="lawn-ordinance-meta">
          <div>
            <dt>Municipality</dt>
            <dd>{ordinance.municipality || 'Not set'}</dd>
          </div>
          <div>
            <dt>Allowed hours</dt>
            <dd>
              {formatTimeLabel(ordinance.allowedTimeStart) &&
              formatTimeLabel(ordinance.allowedTimeEnd)
                ? `${formatTimeLabel(ordinance.allowedTimeStart)} – ${formatTimeLabel(
                    ordinance.allowedTimeEnd
                  )}`
                : 'Any time'}
            </dd>
          </div>
        </dl>
      )}

      {verdict.ruleText && <p className="lawn-ordinance-rule">{verdict.ruleText}</p>}
      {ordinance?.notes && <p className="lawn-ordinance-notes">{ordinance.notes}</p>}
    </div>
  );
}

OrdinanceWidget.propTypes = {
  ordinance: PropTypes.object,
  address: PropTypes.string,
  isLoading: PropTypes.bool,
};

OrdinanceWidget.defaultProps = {
  ordinance: null,
  address: '',
  isLoading: false,
};
